import type { SagaIterator } from 'redux-saga';
import { call, getContext, put, takeEvery, select } from 'redux-saga/effects';
import { toUser } from '@peloton/auth/mappers';
import { loadAuthUserFailure, loadAuthUserSuccess } from '@peloton/auth/redux';
import { EXT_LINK_ENV_CONTEXT } from '@peloton/external-links/constants';
import type { ExtLinkEnv } from '@peloton/external-links/models';
import { toLocaleFromHostname } from '@peloton/internationalize/models/locale';
import {
  addCurrentLocaleToUrl,
  checkAndRemoveLocalePrefix,
} from '@peloton/internationalize/models/path';
import { getLocation } from '@peloton/redux/selectors';
import { toMe } from '@members/me/mappers';
import { updateMe } from '@members/me/redux';
import { RESET_PASSWORD_ROUTE } from '@members/pg-auth/urls';
import { persistAttemptedRoute } from '@members/pg-profile-setup/persistAttemptedRoute';
import {
  BENEFITS_WELCOME_PATH,
  PASSWORD_PATH,
  USERNAME_PATH,
  WELCOME_PATH,
} from '@members/pg-profile-setup/urls';
import {
  REMOTE_ACTIVATION_ROUTE,
  ADD_ACCOUNT_ROUTE,
  REMOTE_ACTIVATION_PROFILE_ROUTE,
  REMOTE_ACTIVATION_CHECKOUT_ROUTE,
} from '@members/pg-remote-activation/urls';
import type { loadUserRequestSuccess } from '../redux/userMapper';
import { loadUserSuccess, UserMapperActionType } from '../redux/userMapper';

// TODO: Move to routing package
export const redirect = (url: string) => window.location.assign(url);

let userDidSucceed: boolean = false;

const allowedPaths = [
  WELCOME_PATH,
  PASSWORD_PATH,
  USERNAME_PATH,
  BENEFITS_WELCOME_PATH,
  REMOTE_ACTIVATION_ROUTE,
  ADD_ACCOUNT_ROUTE,
  REMOTE_ACTIVATION_PROFILE_ROUTE,
  REMOTE_ACTIVATION_CHECKOUT_ROUTE,
];

const isAllowedPages = (location: Location) => {
  const pathname = location?.pathname?.replace?.(/\/$/, '');
  // Strip locale prefix (e.g. /de-DE/) before comparing against allowed paths.
  // Guard undefined: checkAndRemoveLocalePrefix calls .split('/') with no null
  // check, so an absent pathname would throw and surface as an auth failure.
  const pathnameWithoutLocale = pathname
    ? checkAndRemoveLocalePrefix(pathname)
    : pathname;
  return (
    allowedPaths.some(path => pathnameWithoutLocale === path || pathname === path) ||
    pathname?.includes(RESET_PASSWORD_ROUTE)
  );
};

export const userMapperSaga = function* (
  extLinkEnv: ExtLinkEnv,
  action: ReturnType<typeof loadUserRequestSuccess>,
): SagaIterator {
  try {
    const user = action.payload;
    const location = yield select(getLocation);
    // TODO: this logic should be owned by auth, and should be page-specific, but we are updating on-boarding flow soon
    if (!user.username && !isAllowedPages(location)) {
      persistAttemptedRoute();
      // Preserve the current locale prefix in the redirect URL. Locale is read
      // from the URL/hostname (not Redux env) because the members store does
      // not register the env reducer, so getLocale always returns the default.
      const locale = toLocaleFromHostname();
      yield call(redirect, addCurrentLocaleToUrl(WELCOME_PATH, locale));
    } else {
      // Individual mapped user types
      yield put(loadAuthUserSuccess(toUser(user)));
      yield put(updateMe(toMe(user)));
      // Mapping complete
      if (!userDidSucceed) {
        // this was running on every page change causing excessive segment analytics logging
        userDidSucceed = true;
        yield put(loadUserSuccess());
      }
    }
  } catch (e) {
    yield put(loadAuthUserFailure());
  }
};

export default function* (): SagaIterator {
  const extLinkEnv = yield getContext(EXT_LINK_ENV_CONTEXT);
  yield takeEvery(UserMapperActionType.RequestSuccess, userMapperSaga, extLinkEnv);
}
